/**
 * Cart Service V4
 * 
 * 核心简化:
 * - 删除 buffer/pendingOps/flushBuffer 系统
 * - 直接调用 API,立即触发 refresh
 * - Add 转 Update (避免重复行)
 * - 400/404 一次静默重试
 * - 同 lineRef 更新采用 Last intent wins, 被覆盖的写可能被静默 abort
 */

import { useCartStore } from '../store/useCartStore';
import { cartApi } from '../api/cartApi';
import { CartApiError } from '../api/cartApi.types';
import { isSameBusinessLine } from '../utils/cartBusinessLine';
import { ErrorCode } from '../errors/catalog';
import type { CartData, CartSummary, CartSnapshot } from '../types/cartTypes';
import type { Result, AppError, AppErrorContext } from '../errors/types';
import { buildCartSummary } from '../utils/cartCalculations';
import { storefrontApi } from '../api/storefrontApi';
import { t } from '../constants/i18n';
import { parseStockError } from '../utils/cartRules';
import { fetchCartSnapshot, requestCartRefresh } from '../bootstrap/refreshCoordinator';
import { generateUuid } from '../lib/uuid';
import { normalizeError } from '../errors/normalizer';

type UpdateTask = {
  lineRef: string;
  targetQty: number;
  waiters: Array<(result: Result<void>) => void>;
};

const writeQueue = new Map<string, UpdateTask>();
let isWriting = false;
let idleResolvers: Array<() => void> = [];
let inFlightLineRef: string | null = null;
const activeRequestControllers = new Map<string, AbortController>();

function supportsAbortController(): boolean {
  return typeof AbortController !== 'undefined';
}


function scheduleProcessQueue() {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(processQueue);
  } else {
    Promise.resolve().then(processQueue);
  }
}

function resolveIdleIfReady() {
  if (isWriting || writeQueue.size > 0) return;
  const resolvers = idleResolvers;
  idleResolvers = [];
  resolvers.forEach((resolve) => resolve());
  useCartStore.getState().setLoading(false);
}

function waitForIdle(): Promise<void> {
  if (!isWriting && writeQueue.size === 0) return Promise.resolve();
  return new Promise((resolve) => idleResolvers.push(resolve));
}

/**
 * 将更新任务加入写队列
 * 
 * 逻辑：
 * - 检查队列中是否已存在针对同一 item 的任务
 * - 若存在，更新目标 quantity 并合并 waiters（防抖）
 * - 若不存在，创建新任务
 * - 调度队列处理
 * 
 * @param lineRef - 行项目唯一标识 (__clientLineId 或 key)
 * @param targetQty - 目标数量
 * @returns Promise<Result<void>> - 任务完成后的结果
 */
function enqueueUpdate(lineRef: string, targetQty: number): Promise<Result<void>> {
  return new Promise((resolve) => {
    const existing = writeQueue.get(lineRef);
    if (existing) {
      existing.targetQty = targetQty;
      existing.waiters.push(resolve);
    } else {
      writeQueue.set(lineRef, {
        lineRef,
        targetQty,
        waiters: [resolve]
      });
    }
    if (inFlightLineRef === lineRef) {
      const controller = activeRequestControllers.get(lineRef);
      if (controller) {
        controller.abort('Superseded');
      }
    }
    scheduleProcessQueue();
  });
}


/**
 * 添加购物车时的可选参数
 */
export interface AddToCartOptions {
  properties?: Record<string, string>;
  selling_plan?: number;
}

function buildAppError(params: {
  code: ErrorCode;
  message: string;
  level?: 'info' | 'warning' | 'error';
  retryable: boolean;
  context?: AppErrorContext;
  cause?: unknown;
}): AppError {
  return normalizeError({
    code: params.code,
    message: params.message,
    level: params.level || (params.retryable ? 'warning' : 'error'),
    retryable: params.retryable,
    timestamp: Date.now(),
    context: params.context,
    cause: params.cause
  });
}

function normalizeDiscountCode(code: string): string {
  return code.trim().toUpperCase();
}

function dedupeDiscountCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  codes.forEach((raw) => {
    const normalized = normalizeDiscountCode(raw);
    if (!normalized || normalized === '0') return;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function getActiveDiscountCodes(
  snapshot: CartSnapshot | CartData | null,
  fallbackCodes: string[] = []
): string[] {
  if (!snapshot) return dedupeDiscountCodes(fallbackCodes);
  const fromCodes = (snapshot.discount_codes ?? [])
    .filter((entry) => entry.code && entry.code !== '0' && entry.applicable)
    .map((entry) => entry.code);
  const fromApplications = (snapshot.cart_level_discount_applications ?? [])
    .filter((discount) => discount.type === 'discount_code')
    .map((discount) => discount.title ?? '')
    .filter((code) => code && code !== '0');
  const merged = dedupeDiscountCodes([...fromCodes, ...fromApplications]);
  return merged.length > 0 ? merged : dedupeDiscountCodes(fallbackCodes);
}

function isDiscountCodeApplied(cart: CartData, targetCode: string): boolean {
  const normalizedTarget = normalizeDiscountCode(targetCode);
  const appliedCodes = (cart.cart_level_discount_applications ?? [])
    .filter((discount) => discount.type === 'discount_code')
    .map((discount) => discount.title ?? '')
    .filter(Boolean)
    .map((code) => normalizeDiscountCode(code));
  if (appliedCodes.includes(normalizedTarget)) return true;
  const applicableCodes = (cart.discount_codes ?? [])
    .filter((entry) => entry.applicable)
    .map((entry) => normalizeDiscountCode(entry.code));
  return applicableCodes.includes(normalizedTarget);
}

/**
 * 执行单个更新任务
 * 
 * 流程：
 * 1. 查找 item key (优先从 serverCart 找，再从 uiCart 找)
 * 2. 如果找不到 key，报错 itemNotFound
 * 3. 调用 API updateLineItem
 * 4. 错误处理：
 *    - 400/404: 触发一次 Key Drift Retry (静默 Refresh + 重试)
 *    - 其他错误: 抛出
 * 5. 成功后触发 requestCartRefresh
 * 6. 执行库存校验 (Stock Limit Check)
 * 
 * @param task - 更新任务对象
 */
async function runUpdateTask(task: UpdateTask): Promise<Result<void>> {
  const store = useCartStore.getState();
  const { lineRef, targetQty } = task;
  const controller = supportsAbortController() ? new AbortController() : null;
  if (controller) {
    activeRequestControllers.set(lineRef, controller);
  }

  try {
    let itemKey: string | undefined;

    if (store.serverCart) {
      const serverItem = store.serverCart.items.find(
        item => (item.properties?.__clientLineId === lineRef) || (item.key === lineRef)
      );
      if (serverItem) {
        itemKey = serverItem.key;
      }
    }

    if (!itemKey) {
      const uiItem = store.uiCart.items.find(
        item => (item.properties?.__clientLineId === lineRef) || (item.key === lineRef)
      );
      if (uiItem) {
        itemKey = uiItem.key;
      }
    }

    if (!itemKey) {
      return {
        ok: false,
        error: buildAppError({
          code: ErrorCode.UNKNOWN,
          message: t('cart.errors.itemNotFound'),
          retryable: false,
          context: { domain: 'cart', operation: 'update' }
        })
      };
    }

    try {
      await cartApi.updateLineItem(itemKey, targetQty, controller?.signal);
    } catch (error) {
      if (controller?.signal.aborted) {
        return { ok: true, data: undefined };
      }
      if (error instanceof CartApiError && (error.status === 400 || error.status === 404)) {
        console.warn(`[cartService.updateLineItem] ${error.status} 错误,执行一次静默 fetch 重试`);

        // key-drift-retry is always silent: no loading indicator for 400/404 recovery.
        const refreshResult = await requestCartRefresh('key-drift-retry', { showLoading: false });
        if (!refreshResult.ok) {
          return { ok: false, error: refreshResult.error };
        }

        const freshStore = useCartStore.getState();
        const freshItem = freshStore.serverCart?.items.find(
          item => (item.properties?.__clientLineId === lineRef) || (item.key === lineRef)
        );

        if (!freshItem) {
          throw error;
        }

        await cartApi.updateLineItem(freshItem.key, targetQty, controller?.signal);
      } else {
        throw error;
      }
    }

    const refreshResult = await requestCartRefresh('after-write');
    if (!refreshResult.ok) {
      return { ok: false, error: refreshResult.error };
    }

    const freshStore = useCartStore.getState();
    const serverItem = freshStore.serverCart?.items.find(
      item => (item.properties?.__clientLineId === lineRef) || (item.key === lineRef)
    );

    if (serverItem && serverItem.quantity < targetQty) {
      store.setUiItemQuantity(lineRef, serverItem.quantity);

      const error = buildAppError({
        code: ErrorCode.CART_STOCK_LIMIT,
        message: t('cart.errors.stockLimit', serverItem.quantity.toString()),
        level: 'warning',
        retryable: false,
        context: {
          domain: 'cart',
          operation: 'update',
          maxQty: serverItem.quantity,
          requestedQty: targetQty
        }
      });

      return { ok: false, error };
    }

    return { ok: true, data: undefined };
  } catch (error) {
    console.error('[cartService.updateLineItem] Error:', error);

    if (controller?.signal.aborted) {
      return { ok: true, data: undefined };
    }

    if (error instanceof CartApiError) {
      const context: AppErrorContext = {
        domain: 'cart',
        operation: 'update',
        status: error.status
      };
      let code: ErrorCode | undefined;
      let parsedContext: AppErrorContext | undefined;

      if (error.type === 'SHOPIFY_VALIDATION') {
        const parsed = parseStockError(error.message);
        if (parsed.isStockError) {
          code = ErrorCode.CART_STOCK_LIMIT;
          parsedContext = {
            maxQty: parsed.maxQty,
            requestedQty: targetQty
          };
          if (typeof parsed.maxQty === 'number') {
            store.setUiItemQuantity(lineRef, parsed.maxQty);
          }
          void requestCartRefresh('stock-limit', { showLoading: false });
        } else {
          code = ErrorCode.CART_REJECTED_VALIDATION;
        }
      }

      return {
        ok: false,
        error: normalizeError(error, {
          code,
          context: { ...context, ...(parsedContext ?? {}) }
        })
      };
    }

    return {
      ok: false,
      error: normalizeError(error, {
        code: ErrorCode.UNKNOWN,
        message: t('cart.errors.updateFailed'),
        retryable: false,
        context: { domain: 'cart', operation: 'update' }
      })
    };
  }
  finally {
    if (controller) {
      activeRequestControllers.delete(lineRef);
    }
  }
}

/**
 * 处理写队列 (串行消费)
 * 
 * 逻辑：
 * - 每次取出队列头部任务执行
 * - 执行完成后通知所有 waiters
 * - 如果队列中该任务已被更新（debounce），则继续处理
 * - 队列清空后解除 Loading 状态
 */
async function processQueue() {
  if (isWriting) return;

  const nextKey = writeQueue.keys().next().value as string | undefined;
  if (!nextKey) {
    resolveIdleIfReady();
    return;
  }

  const task = writeQueue.get(nextKey);
  if (!task) {
    writeQueue.delete(nextKey);
    scheduleProcessQueue();
    return;
  }

  isWriting = true;
  useCartStore.getState().setLoading(true);
  inFlightLineRef = nextKey;

  // 捕获本次实际处理的 qty，用于后续判断是否有新值入队
  const processedQty = task.targetQty;

  const result = await runUpdateTask(task);
  task.waiters.forEach((resolve) => resolve(result));

  // 仅当队列中的 targetQty 与本次实际处理的值相同时才移除
  // 如果在 runUpdateTask 执行期间有新值入队，则保留队列项继续处理
  const current = writeQueue.get(nextKey);
  if (current && current.targetQty === processedQty) {
    writeQueue.delete(nextKey);
  }

  isWriting = false;
  inFlightLineRef = null;
  scheduleProcessQueue();
  resolveIdleIfReady();
}

/**
 * Cart Service V4 - 简化版
 */
export const cartService = {
  /**
   * 获取购物车权威快照并写入 Store
   */
  fetchCart: async (): Promise<Result<CartData>> => {
    return fetchCartSnapshot(true);
  },

  /**
   * V4: 添加商品 (Add 转 Update 逻辑)
   * 
   * @param variantId - 变体 ID
   * @param quantity - 数量
   * @param options - 可选参数 (properties, selling_plan)
   */
  addToCart: async (
    variantId: number,
    quantity: number = 1,
    options?: AddToCartOptions
  ): Promise<Result<void>> => {
    const store = useCartStore.getState();
    store.setLoading(true);
    let locked = false;
    let deferLoadingClear = false;

    try {
      // Step 1: 检查是否已存在同款商品 (Add 转 Update)
      const existingItem = store.uiCart.items.find(item => {
        // 构造临时 item 用于比较
        const tempItem = {
          id: variantId,
          properties: options?.properties,
          selling_plan_allocation: options?.selling_plan
            ? { selling_plan_id: options.selling_plan }
            : undefined
        };
        return isSameBusinessLine(item as any, tempItem as any);
      });

      if (existingItem) {
        // 转为 Update
        const lineRef = existingItem.properties?.__clientLineId ?? existingItem.key;
        const targetQty = existingItem.quantity + quantity;
        console.log(`[cartService.addToCart] 同款商品已存在,转为 Update: ${lineRef}, 目标数量: ${targetQty}`);
        deferLoadingClear = true;
        return await cartService.updateLineItem(lineRef, targetQty);
      }

      await waitForIdle();
      isWriting = true;
      locked = true;

      // Step 2: 不存在,执行 Add
      const __clientLineId = generateUuid();
      const properties = {
        ...(options?.properties ?? {}),
        __clientLineId
      };

      await cartApi.addToCart({
        items: [{
          id: variantId,
          quantity,
          properties,
          selling_plan: options?.selling_plan
        }]
      });

      // Step 3: 触发 refresh
      const refreshResult = await requestCartRefresh('after-write');
      if (!refreshResult.ok) {
        return { ok: false, error: refreshResult.error };
      }

      return { ok: true, data: undefined };

    } catch (error) {
      console.error('[cartService.addToCart] Error:', error);

      if (error instanceof CartApiError) {
        const code = error.type === 'SHOPIFY_VALIDATION'
          ? ErrorCode.CART_REJECTED_VALIDATION
          : undefined;
        return {
          ok: false,
          error: normalizeError(error, {
            code,
            context: {
              domain: 'cart',
              operation: 'add',
              status: error.status
            }
          })
        };
      }

      return {
        ok: false,
        error: normalizeError(error, {
          code: ErrorCode.UNKNOWN,
          message: t('cart.errors.addFailed'),
          retryable: false,
          context: { domain: 'cart', operation: 'add' }
        })
      };
    } finally {
      if (!deferLoadingClear) {
        store.setLoading(false);
      }
      if (locked) {
        isWriting = false;
        scheduleProcessQueue();
        resolveIdleIfReady();
      }
    }
  },

  /**
   * V4: 更新行项目数量 (含 400/404 一次重试)
   * 
   * @param lineRef - __clientLineId 或 item.key
   * @param targetQty - 目标数量
   */
  updateLineItem: async (lineRef: string, targetQty: number): Promise<Result<void>> => {
    const store = useCartStore.getState();
    store.setLoading(true);
    store.touchUiEdit();
    store.setUiItemQuantity(lineRef, targetQty);
    return enqueueUpdate(lineRef, targetQty);
  },

  /**
   * V4: 删除行项目
   */
  removeLineItem: async (lineRef: string): Promise<Result<void>> => {
    return cartService.updateLineItem(lineRef, 0);
  },

  /**
   * 获取购物车摘要
   */
  getCartSummary: (): CartSummary => {
    const state = useCartStore.getState();
    return buildCartSummary({
      totalPriceCents: state.total_price,
      originalTotalPriceCents: state.original_total_price,
      totalDiscountCents: state.total_discount,
      itemCount: state.count,
      currency: state.currency
    });
  },

  /**
   * 校验折扣码 (Shadow Validation)
   */
  validateDiscountCode: async (code: string): Promise<Result<void>> => {
    if (!code) return { ok: true, data: undefined };

    const store = useCartStore.getState();
    store.setDiscountCodeState({ isValidating: true, error: null });

    try {
      const result = await storefrontApi.validateDiscount(store.items, code);

      if (result.isValid) {
        store.setDiscountCodeState({
          isValidating: false,
          code,
          isValid: true,
          discountAmount: result.discountAmount,
          error: null
        });

        return { ok: true, data: undefined };
      }

      store.setDiscountCodeState({
        isValidating: false,
        isValid: false,
        error: result.error || t('cart.errors.couponInvalid')
      });

      return {
        ok: false,
        error: buildAppError({
          code: ErrorCode.DISCOUNT_CODE_INVALID,
          message: result.error || t('cart.errors.couponInvalid'),
          retryable: false,
          context: {
            domain: 'discount',
            discountCode: code,
            apiMessage: result.error,
            operation: 'validateDiscountCode'
          }
        })
      };

    } catch (error) {
      store.setDiscountCodeState({
        isValidating: false,
        isValid: false,
        error: t('cart.errors.couponValidationFailed')
      });

      return {
        ok: false,
        error: buildAppError({
          code: ErrorCode.DISCOUNT_CODE_FAILED,
          message: t('cart.errors.couponValidationFailed'),
          retryable: true,
          context: {
            domain: 'discount',
            discountCode: code,
            operation: 'validateDiscountCode'
          },
          cause: error
        })
      };
    }
  },

  /**
   * 应用折扣码 (Ajax + refresh)
   */
  applyDiscountCode: async (code: string): Promise<Result<void>> => {
    const trimmed = code.trim();
    if (!trimmed) return { ok: true, data: undefined };

    const store = useCartStore.getState();
    store.setDiscountCodeState({ isValidating: true, error: null });

    const normalizedInput = normalizeDiscountCode(trimmed);
    const existingCodes = getActiveDiscountCodes(
      store.serverCart ?? store.uiCart,
      store.discountCode.appliedCodes
    );
    const mergedCodes = dedupeDiscountCodes([...existingCodes, normalizedInput]);

    try {
      await cartApi.updateDiscountCodes(mergedCodes.join(','));
      const refreshResult = await fetchCartSnapshot(true);
      if (!refreshResult.ok) {
        store.setDiscountCodeState({ isValidating: false });
        return { ok: false, error: refreshResult.error };
      }
      const cart = refreshResult.data;
      store.setCart(cart, { source: 'discount-apply' });
      const appliedCodes = getActiveDiscountCodes(cart, mergedCodes);

      if (!isDiscountCodeApplied(cart, normalizedInput)) {
        store.setDiscountCodeState({
          isValidating: false,
          isValid: false,
          error: t('cart.errors.couponInvalid')
        });

        return {
          ok: false,
          error: normalizeError(new Error('Discount code not applied'), {
            code: ErrorCode.DISCOUNT_CODE_INVALID,
            message: t('cart.errors.couponInvalid'),
            retryable: false,
            context: {
              domain: 'discount',
              discountCode: trimmed,
              operation: 'applyDiscountCode'
            }
          })
        };
      }

      store.setDiscountCodeState({
        isValidating: false,
        code: normalizedInput,
        appliedCodes,
        isValid: true,
        error: null
      });

      return { ok: true, data: undefined };
    } catch (error) {
      const isValidationError =
        error instanceof CartApiError && error.type === 'SHOPIFY_VALIDATION';
      const message = isValidationError
        ? t('cart.errors.couponInvalid')
        : t('cart.errors.couponValidationFailed');

      store.setDiscountCodeState({
        isValidating: false,
        isValid: false,
        error: message
      });

      return {
        ok: false,
        error: normalizeError(error, {
          code: isValidationError ? ErrorCode.DISCOUNT_CODE_INVALID : ErrorCode.DISCOUNT_CODE_FAILED,
          message,
          retryable: !isValidationError,
          context: {
            domain: 'discount',
            discountCode: trimmed,
            operation: 'applyDiscountCode',
            status: error instanceof CartApiError ? error.status : undefined
          }
        })
      };
    }
  },

  /**
   * 移除单个折扣码 (Ajax + refresh)
   */
  removeDiscountCode: async (code: string): Promise<Result<void>> => {
    const store = useCartStore.getState();
    const currentCode = store.discountCode.code ?? undefined;
    const normalizedTarget = normalizeDiscountCode(code);
    store.setDiscountCodeState({ isValidating: true, error: null });

    const existingCodes = getActiveDiscountCodes(
      store.serverCart ?? store.uiCart,
      store.discountCode.appliedCodes
    );
    const remainingCodes = dedupeDiscountCodes(existingCodes).filter((entry) => entry !== normalizedTarget);

    try {
      await cartApi.updateDiscountCodes(remainingCodes.join(','));
      const refreshResult = await fetchCartSnapshot(true);
      if (!refreshResult.ok) {
        store.setDiscountCodeState({ isValidating: false });
        return { ok: false, error: refreshResult.error };
      }
      store.setCart(refreshResult.data, { source: 'discount-remove' });
      const appliedCodes = getActiveDiscountCodes(refreshResult.data, remainingCodes);
      if (currentCode && normalizeDiscountCode(currentCode) === normalizedTarget) {
        store.resetDiscountCode();
      } else {
        store.setDiscountCodeState({
          isValidating: false,
          appliedCodes,
          error: null
        });
      }
      return { ok: true, data: undefined };
    } catch (error) {
      const message = t('cart.errors.couponValidationFailed');
      store.setDiscountCodeState({
        isValidating: false,
        error: message
      });

      return {
        ok: false,
        error: normalizeError(error, {
          code: ErrorCode.DISCOUNT_CODE_FAILED,
          message,
          retryable: true,
          context: {
            domain: 'discount',
            discountCode: currentCode,
            operation: 'clearDiscountCodes',
            status: error instanceof CartApiError ? error.status : undefined
          }
        })
      };
    }
  },

  /**
   * 清空所有折扣码 (Ajax + refresh)
   */
  clearDiscountCodes: async (): Promise<Result<void>> => {
    const store = useCartStore.getState();
    const currentCode = store.discountCode.code ?? undefined;
    store.setDiscountCodeState({ isValidating: true, error: null });

    try {
      await cartApi.updateDiscountCodes('');
      const refreshResult = await fetchCartSnapshot(true);
      if (!refreshResult.ok) {
        store.setDiscountCodeState({ isValidating: false });
        return { ok: false, error: refreshResult.error };
      }
      store.setCart(refreshResult.data, { source: 'discount-clear' });
      store.resetDiscountCode();
      return { ok: true, data: undefined };
    } catch (error) {
      const message = t('cart.errors.couponValidationFailed');
      store.setDiscountCodeState({
        isValidating: false,
        error: message
      });

      return {
        ok: false,
        error: normalizeError(error, {
          code: ErrorCode.DISCOUNT_CODE_FAILED,
          message,
          retryable: true,
          context: {
            domain: 'discount',
            discountCode: currentCode,
            operation: 'removeDiscountCode',
            status: error instanceof CartApiError ? error.status : undefined
          }
        })
      };
    }
  }
};
